<div class="space-y-6">
  <div class="rounded-2xl border border-base-300 bg-base-100 p-6 lg:p-8">
    <div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
      <div class="space-y-2 max-w-2xl">
        <p class="text-xs font-semibold uppercase tracking-[0.2em] text-base-content/45">
          Access control
        </p>
        <h2 class="text-3xl font-black tracking-tight lg:text-4xl">
          Team &amp; accounts
        </h2>
        <p class="text-sm leading-6 text-base-content/60">
          Super Admin creates staff, rider and customer accounts. Disabled accounts
          are blocked on admin, rider, and customer apps.
        </p>
      </div>

      <button class="btn btn-primary" (click)="addUser()">
        <i class="fa-solid fa-user-plus"></i>
        Create account
      </button>
    </div>

    <div class="mt-8 grid grid-cols-2 gap-3 md:grid-cols-5">
      <div class="rounded-xl border border-base-300 bg-base-200/40 p-4">
        <p class="text-[11px] uppercase tracking-wider text-base-content/45">Total</p>
        <p class="mt-1 text-2xl font-black">{{ total }}</p>
      </div>
      <div class="rounded-xl border border-base-300 bg-base-200/40 p-4">
        <p class="text-[11px] uppercase tracking-wider text-base-content/45">Active</p>
        <p class="mt-1 text-2xl font-black text-success">{{ activeRate() }}%</p>
      </div>
      <div class="rounded-xl border border-base-300 bg-base-200/40 p-4">
        <p class="text-[11px] uppercase tracking-wider text-base-content/45">Admins</p>
        <p class="mt-1 text-2xl font-black">{{ (roleCounts['store_manager'] || 0) + (roleCounts['superadmin'] || 0) + (roleCounts['picker'] || 0) + (roleCounts['inventory_operator'] || 0) + (roleCounts['finance'] || 0) }}</p>
      </div>
      <div class="rounded-xl border border-base-300 bg-base-200/40 p-4">
        <p class="text-[11px] uppercase tracking-wider text-base-content/45">Riders</p>
        <p class="mt-1 text-2xl font-black">{{ roleCounts['rider'] || 0 }}</p>
      </div>
      <div class="rounded-xl border border-base-300 bg-base-200/40 p-4 col-span-2 md:col-span-1">
        <p class="text-[11px] uppercase tracking-wider text-base-content/45">Customers</p>
        <p class="mt-1 text-2xl font-black">{{ roleCounts['user'] || 0 }}</p>
      </div>
    </div>
  </div>

  <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
    <div class="relative w-full lg:max-w-md">
      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-base-content/40">
        <i class="fa-solid fa-magnifying-glass"></i>
      </div>
      <input
        type="text"
        class="input input-bordered w-full pl-10"
        placeholder="Search name, email, or phone..."
        [(ngModel)]="search"
        (ngModelChange)="onSearch()"
      />
    </div>

    <div class="flex flex-wrap gap-2">
      @for (f of filters; track f.id) {
        <button
          type="button"
          class="btn btn-sm"
          [class.btn-primary]="roleFilter === f.id"
          [class.btn-ghost]="roleFilter !== f.id"
          (click)="setRoleFilter(f.id)"
        >
          {{ f.label }}
          @if (f.id !== 'all') {
            <span class="opacity-60">{{ roleCounts[f.id] || 0 }}</span>
          }
        </button>
      }
    </div>
  </div>

  <div class="card border border-base-300 bg-base-100 overflow-hidden relative min-h-[420px]">
    @if (loading) {
      <div class="absolute inset-0 z-10 bg-base-100/60 flex items-center justify-center">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>
    }

    <div class="overflow-x-auto">
      <table class="table w-full">
        <thead class="bg-base-200">
          <tr>
            <th class="cursor-pointer" (click)="onSort('name')">Person</th>
            <th>Contact</th>
            <th class="cursor-pointer" (click)="onSort('role')">Role</th>
            <th>Status</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (!loading && users.length === 0) {
            <tr>
              <td colspan="5" class="text-center py-16 text-base-content/45">
                No accounts match this filter.
              </td>
            </tr>
          }

          @for (user of users; track user.id) {
            <tr class="hover:bg-base-200/40">
              <td>
                <div class="font-semibold">{{ user.name || 'Unnamed' }}</div>
                <div class="text-[11px] font-mono text-base-content/45">
                  {{ user.id?.substring(0, 8) }}
                </div>
              </td>
              <td>
                <div class="text-sm">{{ user.phone || '—' }}</div>
                <div class="text-xs text-base-content/55">{{ user.email || '—' }}</div>
              </td>
              <td>
                <div class="flex flex-wrap gap-1">@for (role of userRoles(user); track role) {
                  <span class="badge badge-sm uppercase tracking-wide" [ngClass]="getRoleBadgeClass(role)">{{ roleLabel(role) }}</span>
                }</div>
              </td>
              <td>
                @if (user.is_active) {
                  <span class="badge badge-success badge-outline badge-sm">Active</span>
                } @else {
                  <span class="badge badge-error badge-outline badge-sm">Disabled</span>
                }
              </td>
              <td class="text-right">
                <div class="inline-flex gap-1">
                  <button class="btn btn-ghost btn-sm" (click)="editUser(user)" title="Edit">
                    <i class="fa-regular fa-pen-to-square"></i>
                  </button>
                  <button
                    class="btn btn-ghost btn-sm"
                    [class.text-error]="user.is_active"
                    [class.text-success]="!user.is_active"
                    [disabled]="togglingId === user.id"
                    (click)="toggleUserStatus(user)"
                    [title]="user.is_active ? 'Disable account' : 'Restore access'"
                  >
                    @if (togglingId === user.id) {
                      <span class="loading loading-spinner loading-xs"></span>
                    } @else {
                      <i class="fa-solid" [ngClass]="user.is_active ? 'fa-ban' : 'fa-rotate-left'"></i>
                    }
                  </button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="border-t border-base-300 p-4 flex items-center justify-between gap-4">
      <p class="text-sm text-base-content/55">
        Page <span class="font-semibold text-base-content">{{ page }}</span>
        of {{ totalPages || 1 }}
      </p>
      <div class="join">
        <button class="join-item btn btn-sm" (click)="prevPage()" [disabled]="page <= 1">
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <button class="join-item btn btn-sm" (click)="nextPage()" [disabled]="page >= totalPages">
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</div>
